import { Loader } from 'components/Loader';
import { type DataTableFiltersApi } from 'components/DataTable';
import { confirmPopup } from 'components/Popups/Confirm';
import { selectOptionPopup } from 'components/Popups/SelectOption';
import {
    useGetAllRegularTotemClosedDayQuery,
    useGetDeliveryPeopleQuery,
    useGetDynamicDeliveryPlanningRowsForDateQuery,
    useRegenerateDynamicDeliveryPlanningsMutation,
    useUpdateDynamicDeliveryPlanningBatchMutation,
    useUpdateDynamicDeliveryPlanningDelivererMutation,
    useReorderDynamicDeliveryPlanningTourMutation,
} from 'data/__generated__';
import { formatDateAsAnniversary, dateFromString, formatDurationMinutes } from 'helpers/dateTimes';
import { ArrowDownToLine, ChevronDown, ListPlus, Users, X } from 'lucide-react';
import { type DropResult } from '@hello-pangea/dnd';
import { type ExpandedState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { type RouteMode } from 'components/LeafletMap';

import { Button } from '@/components/ui/button';

import { PlanningHeader } from './components/PlanningHeader';
import { PlanningActionsToolbar } from './components/PlanningActionsToolbar';
import { PlanningMapView } from './components/PlanningMapView';
import { PlanningMoveRecap } from './components/PlanningMoveRecap';
import { PlanningTableView } from './components/PlanningTableView';
import {
    orderedRegroupementPlanningIds,
    orderedRegroupementsOf,
    parseRegroupementKey,
    planningIdsOfRegroupement,
    regroupementKey,
} from './components/PlanningTourList';
import { SummaryCards, type TourSummary } from './components/SummaryCards';
import { buildZoneColorByReferent } from './constants/approColors';
import {
    UNASSIGNED_DROPPABLE_ID,
    aggregatePlanningRows,
    approIdOf,
    buildDynamicDeliveryColumns,
    buildTourStops,
    estimateTour,
    regroupementRankLabel,
    type DynamicDeliveryFilter,
    type DynamicDeliveryRow,
    type PlanningTableRow,
} from './constants/dynamicDeliveryColumns';
import {
    POTENTIAL_DISPLAY_LIMIT,
    VIEW_OPTIONS,
    addBusinessDays,
    collectFilterOptions,
    delivererName,
    startOfLocalDay,
    type ViewMode,
} from './constants/planningView';

type ParamTypes = Record<string, string | undefined> & {
    selectedDate?: string;
};

// Before/after summary shown in the move confirmation: what's being moved (by site + current appro) and how each
// affected tour changes (stops + estimated duration). The "after" is simulated by reassigning the moving rows to
// the target appro and rebuilding the shared tour stops — no mutation, just a preview.
const buildMovePreviewBody = (rows: PlanningTableRow[], movingIds: string[], toApproId: string): ReactNode => {
    const movingSet = new Set(movingIds);
    const target = rows.find((row) => row.assignedDeliverer?._id === toApproId)?.assignedDeliverer ?? null;
    const deliver = rows.filter((row) => row.isDeliver);

    // Moving rows can include still-potential columns; the move activates them, so the "after" must count them
    // on the target tour even though they aren't delivered yet. "Before" stays today's delivery untouched.
    const movingRows = rows.filter((row) => movingSet.has(row._id));
    const stayed = deliver.filter((row) => !movingSet.has(row._id));
    const movedToTarget = movingRows.map((row) => ({ ...row, isDeliver: true, assignedDeliverer: target }));

    const before = buildTourStops(deliver);
    const after = buildTourStops([...stayed, ...movedToTarget]);

    // Only tours that LOSE delivered rows shrink — a moving potential column doesn't shrink any existing tour.
    const affected = new Set<string>([toApproId]);
    for (const row of deliver) if (movingSet.has(row._id)) affected.add(approIdOf(row));

    const nameOf = (approId: string) =>
        rows.find((row) => approIdOf(row) === approId)?.assignedDeliverer?.profile.fullname ?? 'Non assigné';

    const movingSites = [...new Map(movingRows.map((row) => [row.site?._id ?? row._id, row])).values()];

    return (
        <PreviewBody>
            <PreviewBlock>
                <PreviewTitle>Déplacé</PreviewTitle>
                {movingSites.map((row) => (
                    <PreviewLine key={row._id}>
                        • {row.site?.name ?? '—'}{' '}
                        <PreviewFrom>({row.assignedDeliverer?.profile.fullname ?? 'Non assigné'})</PreviewFrom>
                    </PreviewLine>
                ))}
            </PreviewBlock>
            <PreviewBlock>
                <PreviewTitle>Avant → après</PreviewTitle>
                {[...affected].map((approId) => {
                    const b = before.get(approId) ?? [];
                    const a = after.get(approId) ?? [];
                    const be = estimateTour(b);
                    const ae = estimateTour(a);
                    return (
                        <PreviewRow key={approId}>
                            <span>{nameOf(approId)}</span>
                            <PreviewImpact $over={ae.isOverloaded}>
                                {b.length} arrêt{b.length > 1 ? 's' : ''} · ≈{formatDurationMinutes(be.totalMinutes)} →{' '}
                                {a.length} arrêt{a.length > 1 ? 's' : ''} · ≈{formatDurationMinutes(ae.totalMinutes)}
                                {ae.isOverloaded ? ' ⚠' : ''}
                            </PreviewImpact>
                        </PreviewRow>
                    );
                })}
            </PreviewBlock>
        </PreviewBody>
    );
};

// The activation updates for any still-potential columns among `ids` (empty if all are already active). Shared by
// « Affecter à un opérateur » and « Déplacer vers un arrêt… », which both pull columns into a real tour.
const potentialActivationUpdates = (
    ids: string[],
    rowsById: Map<string, DynamicDeliveryRow>,
    date: string,
): Array<{ columnId: string; date: string; isActive: boolean }> =>
    ids
        .map((id) => rowsById.get(id))
        .filter((row): row is DynamicDeliveryRow => !!row && !row.isActive)
        .map((row) => ({ columnId: row.columnId, date, isActive: true }));

// The move/assign mutations return only the plannings they actually touched; a regeneration mid-edit can drop some
// (the backend writes the rest). Warn instead of a misleading full-success toast. Returns true when it warned.
const warnIfPartialMove = (requested: number, affected: number): boolean => {
    if (affected >= requested) return false;
    toast.warning(
        `${affected}/${requested} colonne(s) traitées — certaines n'existent plus (planning régénéré ?). Réessayez.`,
    );
    return true;
};

export const DynamicDeliveryPlanning = () => {
    const navigate = useNavigate();
    const { selectedDate: selectedDateFromUrl } = useParams<ParamTypes>();

    const {
        loading: totemClosedDaysLoading,
        data: totemClosedDaysData,
        error: totemClosedDaysError,
    } = useGetAllRegularTotemClosedDayQuery({ fetchPolicy: 'cache-and-network' });
    const totemClosedDays = totemClosedDaysData?.getAllRegularTotemClosedDay ?? [];

    const selectedDate = useMemo(() => {
        if (selectedDateFromUrl) {
            return startOfLocalDay(dateFromString(selectedDateFromUrl, true));
        }
        return addBusinessDays(new Date(), 1, totemClosedDays);
    }, [selectedDateFromUrl, totemClosedDays]);

    const dateString = formatDateAsAnniversary({ dateTime: selectedDate, useNewFormat: true });

    const { loading, data, error, refetch } = useGetDynamicDeliveryPlanningRowsForDateQuery({
        variables: { date: dateString },
        fetchPolicy: 'cache-and-network',
        skip: !selectedDateFromUrl && totemClosedDaysLoading,
    });

    const [updatePlanningBatch, { loading: saveLoading }] = useUpdateDynamicDeliveryPlanningBatchMutation();
    const [regeneratePlannings, { loading: regenerating }] = useRegenerateDynamicDeliveryPlanningsMutation();
    const [reassignDeliverer] = useUpdateDynamicDeliveryPlanningDelivererMutation();
    const [reorderTour] = useReorderDynamicDeliveryPlanningTourMutation();

    // All deliverer users: operator picker (« Affecter à un opérateur ») + "add an appro" drop target in the map view.
    const { data: deliveryPeopleData } = useGetDeliveryPeopleQuery();
    const deliveryPeople = useMemo(() => deliveryPeopleData?.getDeliveryPeople ?? [], [deliveryPeopleData]);
    const delivererNameById = useMemo(
        () => new Map(deliveryPeople.map((person) => [person._id, delivererName(person)])),
        [deliveryPeople],
    );

    const rows: DynamicDeliveryRow[] = data?.getDynamicDeliveryPlanningRowsForDate ?? [];

    const [view, setView] = useState<ViewMode>('appro');
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    // « Ajouter à une tournée existante » / « Affecter à la suite d'un point »: the planning ids being moved
    // while the planner picks a target stop. Kept apart from `selectedKeys` because switching views clears it.
    const [insertIds, setInsertIds] = useState<string[]>([]);
    // Pick-a-target sub-step is active whenever there's a pending payload to drop after a stop.
    const insertMode = insertIds.length > 0;
    // Carte "edit mode": a single mode that (1) switches the left pane to a side-by-side board of the focused
    // tours (map kept on the right) and (2) makes regroupements clickable to (de)select them in bulk
    // (key = `${approId}|${rank}`) — move / reorder.
    const [editMode, setEditMode] = useState(false);
    // Ordre de sélection (mode édition) : chaque entrée = les clés `${approId}|${rank}` d'UN arrêt cliqué,
    // dans l'ordre de clic. Un site multi-rank ⇒ plusieurs clés mais un seul numéro (toggé en bloc).
    const [selectionGroups, setSelectionGroups] = useState<string[][]>([]);
    // Carte view: appros whose ordered tours (route + arrows) are emphasised on the map. Empty = show all routes faintly.
    // Multi-select: each selected appro draws its own numbered route, to compare tours side by side when reassigning stops.
    const [focusedApproIds, setFocusedApproIds] = useState<string[]>([]);
    // Carte view: straight-line "vol d'oiseau" by DEFAULT — instant render and NO IGN API call until the user
    // switches to "Itinéraire" (the real road route is consulted on demand). Vol d'oiseau is also the handiest
    // for editing tours (instant recompute on reorder).
    const [mapRouteMode, setMapRouteMode] = useState<RouteMode>('crow');
    const [expanded, setExpanded] = useState<ExpandedState>(true);
    const [allCollapsed, setAllCollapsed] = useState(false);
    const [collapseToken, setCollapseToken] = useState(0);
    const toggleCollapseAll = useCallback(() => {
        setAllCollapsed((prev) => {
            const next = !prev;
            setExpanded(next ? {} : true);
            return next;
        });
        setCollapseToken((token) => token + 1);
    }, []);

    const setSelectedDate = useCallback(
        (date: Date) => {
            navigate(
                `/dynamic-delivery-planning/${formatDateAsAnniversary({
                    dateTime: startOfLocalDay(date),
                    useNewFormat: true,
                })}`,
            );
        },
        [navigate],
    );

    const rowsById = useMemo(() => new Map(rows.map((row) => [row._id, row])), [rows]);

    // The table is grouped, so ticking a group's checkbox also marks the synthesized GROUP row selected — and
    // its id is not a planning id. `selectedKeys` MUST keep them: the table is controlled, and without them
    // TanStack sees the group as unselected so a second click re-selects instead of clearing. Every CONSUMER
    // uses this filtered list instead — counts, and any payload where an id must be a real column. `rows` is
    // the full API set and the table only ever receives a subset, so nothing real is dropped here.
    const selectedColumnIds = useMemo(() => selectedKeys.filter((id) => rowsById.has(id)), [selectedKeys, rowsById]);

    // Columns whose totem has no GPS cannot be routed — kept out of the planning table and surfaced for correction.
    const anomalies = useMemo(() => {
        const byTotem = new Map<
            string,
            { totemId: string; siteName: string; columnCount: number; isActive: boolean }
        >();
        for (const row of rows) {
            if (row.totem) continue;
            const totemId = row.site?.totemId ?? '';
            const existing = byTotem.get(totemId);
            if (existing) {
                existing.columnCount += 1;
                existing.isActive = existing.isActive || row.isActive;
            } else {
                byTotem.set(totemId, {
                    totemId,
                    siteName: row.site?.name ?? 'Site inconnu',
                    columnCount: 1,
                    isActive: row.isActive,
                });
            }
        }
        return Array.from(byTotem.values()).sort((a, b) => Number(b.isActive) - Number(a.isActive));
    }, [rows]);

    const anomalyColumnCount = useMemo(() => anomalies.reduce((sum, a) => sum + a.columnCount, 0), [anomalies]);

    const tableData = useMemo<PlanningTableRow[]>(
        () =>
            rows
                .filter((row) => row.totem != null)
                .map((row) => ({ ...row, isDeliver: row.isActive }))
                .sort(
                    (a, b) =>
                        Number(b.isDeliver) - Number(a.isDeliver) ||
                        a.rank - b.rank ||
                        a.secondaryRank - b.secondaryRank,
                ),
        [rows],
    );

    // Shared aggregation (same function as the group headers) — counts and kg/volume cannot diverge anymore.
    // Computed on the FULL dataset: the summary cards stay true even when the table display is capped below.
    const dayAggregate = useMemo(() => aggregatePlanningRows(tableData), [tableData]);

    // Tour colour = the colour of the zone each deliverer is referent of, built once for the table group headers.
    const approColors = useMemo(() => buildZoneColorByReferent(tableData), [tableData]);

    // Tournées card: same stop sequence + feasibility estimate as the tour list / map (shared helpers).
    const tourSummary = useMemo<TourSummary>(() => {
        const stopsByAppro = buildTourStops(tableData.filter((row) => row.isDeliver));
        const summary: TourSummary = { approCount: stopsByAppro.size, stopCount: 0, overloadedCount: 0 };
        for (const stops of stopsByAppro.values()) {
            summary.stopCount += stops.length;
            if (estimateTour(stops).isOverloaded) summary.overloadedCount += 1;
        }
        return summary;
    }, [tableData]);

    // Live filter state of the table (search + column filters), surfaced via onFiltersChange. Drives the
    // display cap below and the active state of the Urgences summary card.
    const [filters, setFilters] = useState<DynamicDeliveryFilter>({});
    const handleFiltersChange = useCallback((next: DynamicDeliveryFilter) => setFilters(next), []);
    const hasActiveFilters = useMemo(
        () =>
            Object.values(filters).some((value) => {
                if (value == null) return false;
                if (typeof value === 'string') return value.trim() !== '';
                if (Array.isArray(value)) return value.length > 0;
                return value.gte != null || value.lte != null;
            }),
        [filters],
    );

    // Imperative handle on the table's filters, for the clickable summary cards.
    const filtersApiRef = useRef<DataTableFiltersApi | null>(null);
    const URGENT_MOTIFS = useMemo(() => ['Trop ancien', 'Critique'], []);
    const urgentFilterActive = useMemo(
        () =>
            (filters.motifs ?? []).length === URGENT_MOTIFS.length &&
            URGENT_MOTIFS.every((motif) => filters.motifs?.includes(motif)),
        [filters.motifs, URGENT_MOTIFS],
    );
    const toggleUrgentFilter = useCallback(() => {
        filtersApiRef.current?.setFilter('motifs', urgentFilterActive ? undefined : URGENT_MOTIFS);
    }, [urgentFilterActive, URGENT_MOTIFS]);

    // Rows actually fed to the table: all deliver rows + the first POTENTIAL_DISPLAY_LIMIT potential rows
    // (tableData is rank-sorted, so these are the best-ranked candidates). Any active filter or search lifts
    // the cap, so the TanStack column filters always operate on the full dataset.
    const displayData = useMemo<PlanningTableRow[]>(() => {
        if (hasActiveFilters) return tableData;
        const result: PlanningTableRow[] = [];
        let potentials = 0;
        for (const row of tableData) {
            if (!row.isDeliver) {
                potentials += 1;
                if (potentials > POTENTIAL_DISPLAY_LIMIT) continue;
            }
            result.push(row);
        }
        return result;
    }, [tableData, hasActiveFilters]);

    const hiddenPotentialCount = tableData.length - displayData.length;

    // Zone / operator filter options come from the day's rows, so the columns are rebuilt when the day changes.
    const zoneOptions = useMemo(() => collectFilterOptions(tableData, (row) => row.deliveryZone?.name), [tableData]);
    // Assigned deliverer (who actually delivers) — NOT the zone referent.
    const operatorOptions = useMemo(
        () => collectFilterOptions(tableData, (row) => row.assignedDeliverer?.profile.fullname),
        [tableData],
    );
    // Column template types present in the day's rows only (Snacks, Drinks, Fridge…)
    const templateTypeOptions = useMemo(() => collectFilterOptions(tableData, (row) => row.column?.type), [tableData]);
    const columns = useMemo(
        () => buildDynamicDeliveryColumns({ zoneOptions, operatorOptions, templateTypeOptions }),
        [zoneOptions, operatorOptions, templateTypeOptions],
    );

    const clearSelection = useCallback(() => setSelectedKeys([]), []);

    // Clicking a map marker selects every column of that stop (a marker = a site); clicking a tour-list row
    // selects that single column. Re-clicking the same selection clears it. Selection feeds the action toolbar
    // (Affecter à un opérateur / Retirer / Déplacer) so all actions stay usable from the map view.
    const selectColumns = useCallback((ids: string[]) => {
        setSelectedKeys((current) =>
            current.length === ids.length && ids.every((id) => current.includes(id)) ? [] : ids,
        );
    }, []);

    // Clicking an appro header (or its legend entry) toggles its whole ordered tour on the map. Several appros can be
    // emphasised at once (each in its own colour) to compare routes; clicking an active one again removes it.
    const toggleApproFocus = useCallback((approId: string) => {
        setFocusedApproIds((current) =>
            current.includes(approId) ? current.filter((id) => id !== approId) : [...current, approId],
        );
    }, []);

    // Carte « mode édition »: (de)select a whole stop's regroupements (keys = `${approId}|${rank}`). A stop is
    // normally one regroupement, but a multi-rank site is toggled as a unit — select all unless they are all
    // already selected, then deselect all — so a marker (one per site) stays consistent with its highlight.
    const toggleRegroupement = useCallback((approId: string, ranks: number[]) => {
        const keys = ranks.map((rank) => regroupementKey(approId, rank));
        setSelectionGroups((groups) => {
            // Un arrêt = un groupe de clés toggé en bloc : présent ⇒ on le retire, sinon on l'ajoute EN FIN
            // (préserve l'ordre de clic). Comparaison sur l'ensemble de clés du groupe.
            const index = groups.findIndex(
                (group) => group.length === keys.length && keys.every((key) => group.includes(key)),
            );
            return index === -1 ? [...groups, keys] : groups.filter((_, i) => i !== index);
        });
    }, []);

    // Clic sur le titre de la légende : (dé)focus TOUTES les tournées d'un coup. Tout déjà focus ⇒ on défocus
    // tout, sinon on focus tous les appros livreurs du jour.
    const toggleAllApproFocus = useCallback(() => {
        const allApproIds = [...new Set(tableData.filter((row) => row.isDeliver).map((row) => approIdOf(row)))];
        setFocusedApproIds((current) =>
            allApproIds.length > 0 && allApproIds.every((id) => current.includes(id)) ? [] : allApproIds,
        );
    }, [tableData]);

    // Selected regroupements in CLICK order (the planner-defined sequence), each with its resolved planning
    // ids — the single source for the move payload AND the recap chips. One entry per clicked stop.
    const selectedRegroupementsOrdered = useMemo(
        () =>
            selectionGroups.map((keys, index) => {
                const parsed = keys.map(parseRegroupementKey);
                const approId = parsed[0]?.approId ?? '';
                const ranks = parsed.map((part) => part.rank);
                return {
                    order: index + 1,
                    approId,
                    ranks,
                    keys,
                    ids: ranks.flatMap((rank) => planningIdsOfRegroupement(tableData, approId, rank)),
                };
            }),
        [selectionGroups, tableData],
    );

    // Clé de regroupement -> numéro d'ordre de sélection (1-based). Porte appartenance ET numéro : sert au
    // surlignage de la liste (`.has`) et au badge numéroté de la carte.
    const selectionNumberByKey = useMemo(() => {
        const map = new Map<string, number>();
        selectionGroups.forEach((keys, index) => keys.forEach((key) => map.set(key, index + 1)));
        return map;
    }, [selectionGroups]);

    // The move payload: every selected regroupement's planning ids, flattened in tour order. Also feeds the
    // map/list highlight (selected regroupements light up through the existing selected styles).
    const selectedRegroupementIds = useMemo(
        () => selectedRegroupementsOrdered.flatMap((rg) => rg.ids),
        [selectedRegroupementsOrdered],
    );

    // What the map/list highlight as "selected": regroupement payload in edit mode, columns otherwise.
    const mapSelectedKeys = editMode ? selectedRegroupementIds : selectedColumnIds;

    // Recap chips of what's being moved (one per selected regroupement): site name(s) + current appro, removable.
    // Shown while editing AND during the pick-a-target step, so the planner always sees what they're carrying.
    const selectedRegroupementChips = useMemo(
        () =>
            selectedRegroupementsOrdered.map(({ order, approId, ranks, keys, ids }) => {
                const groupRows = ids.map((id) => rowsById.get(id)).filter((row): row is DynamicDeliveryRow => !!row);
                const sites = [...new Set(groupRows.map((row) => row.site?.name ?? '—'))].join(', ');
                const appro =
                    groupRows[0]?.assignedDeliverer?.profile.fullname ??
                    delivererNameById.get(approId) ??
                    'Non assigné';
                return { key: keys.join('+'), order, approId, ranks, sites, appro };
            }),
        [selectedRegroupementsOrdered, rowsById, delivererNameById],
    );

    const toggleEditMode = useCallback(() => {
        // Switching either way starts from a clean slate: drop column selection, regroupement selection and any
        // pending "pick a target" step so the two selection granularities never overlap.
        setSelectedKeys([]);
        setSelectionGroups([]);
        setInsertIds([]);
        setEditMode((on) => !on);
    }, []);

    // « Affecter à la suite d'un point »: snapshot the selected regroupements as the move payload, then the next
    // stop click (map marker / list card) drops them right after it (reuses the pick-target machinery below).
    const startAssignAfterPoint = useCallback(() => {
        if (selectedRegroupementIds.length === 0) return;
        setInsertIds(selectedRegroupementIds);
    }, [selectedRegroupementIds]);

    // « Réordonner dans l'ordre de sélection »: need at least one tour with ≥2 selected stops, else there is
    // nothing to permute (a single stop keeps its slot).
    const canReorder = useMemo(() => {
        const countByAppro = new Map<string, number>();
        for (const rg of selectedRegroupementsOrdered) {
            countByAppro.set(rg.approId, (countByAppro.get(rg.approId) ?? 0) + 1);
        }
        return [...countByAppro.values()].some((count) => count >= 2);
    }, [selectedRegroupementsOrdered]);

    // « Réordonner dans l'ordre de sélection » (mode édition): rewrite each affected tour so its SELECTED stops
    // follow the click order, IN PLACE — the slots they occupy are refilled in click order, unselected stops stay.
    // Per appro (several tours at once). `reorderTour` writes tourOrder = position and the front reads it relatively
    // per appro, so a single flat call spanning appros is enough.
    const handleReorderSelected = useCallback(async () => {
        // Selected ranks per appro, in click order (selectedRegroupementsOrdered is already click-ordered).
        const clickedRanksByAppro = new Map<string, number[]>();
        for (const rg of selectedRegroupementsOrdered) {
            const ranks = clickedRanksByAppro.get(rg.approId) ?? [];
            ranks.push(...rg.ranks);
            clickedRanksByAppro.set(rg.approId, ranks);
        }

        const planningIds: string[] = [];
        const affectedApproNames: string[] = [];
        for (const [approId, clickedRanks] of clickedRanksByAppro) {
            const current = orderedRegroupementsOf(tableData, approId);
            const idsByRank = new Map(current.map((rg) => [rg.rank, rg.ids]));
            // Click order restricted to ranks present in this tour; refills exactly the selected slots.
            const queue = clickedRanks.filter((rank) => idsByRank.has(rank));
            if (queue.length < 2) continue; // a single selected stop can't change this tour's order
            const selectedRanks = new Set(queue);
            let next = 0;
            const reordered = current.map((rg) =>
                selectedRanks.has(rg.rank) ? (idsByRank.get(queue[next++]) ?? rg.ids) : rg.ids,
            );
            planningIds.push(...reordered.flat());
            affectedApproNames.push(
                tableData.find((row) => approIdOf(row) === approId)?.assignedDeliverer?.profile.fullname ??
                    delivererNameById.get(approId) ??
                    'Non assigné',
            );
        }

        if (planningIds.length === 0) return;

        const confirmed = await confirmPopup({
            title: 'Réordonner dans l’ordre de sélection',
            message:
                affectedApproNames.length === 1
                    ? `Réordonner les arrêts sélectionnés de la tournée de ${affectedApproNames[0]} dans l’ordre de sélection ?`
                    : `Réordonner les arrêts sélectionnés dans l’ordre de sélection sur ${affectedApproNames.length} tournées (${affectedApproNames.join(', ')}) ?`,
        });
        if (!confirmed) return;

        try {
            await reorderTour({ variables: { planningIds } });
            await refetch();
            toast.success('Ordre de tournée mis à jour');
            setSelectionGroups([]);
        } catch (err) {
            toast.error(`Erreur lors du réordonnancement : ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [selectedRegroupementsOrdered, tableData, delivererNameById, reorderTour, refetch]);

    // Shared cross-appro insertion (drag handler, « Ajouter à une tournée existante », and the flows below).
    // Reassigns the moved columns to the target appro, then re-persists that appro's tour order with the moved
    // columns spliced in at `insertIndex` — a column put in delivery by hand has no position until this runs.
    // (Caller does the refetch.)
    const reassignAndPosition = useCallback(
        async ({
            planningIds,
            toApproId,
            insertIndex,
        }: {
            planningIds: string[];
            toApproId: string;
            insertIndex: number;
        }) => {
            const reassign = await reassignDeliverer({ variables: { planningIds, delivererId: toApproId } });
            const moving = new Set(planningIds);
            const groups = orderedRegroupementPlanningIds(tableData, toApproId).filter(
                (group) => !group.some((id) => moving.has(id)),
            );
            const at = Math.min(Math.max(insertIndex, 0), groups.length);
            groups.splice(at, 0, planningIds);
            await reorderTour({ variables: { planningIds: groups.flat() } });
            // Report how many rows the reassign actually moved (fewer than requested if some vanished mid-edit).
            return reassign.data?.updateDynamicDeliveryPlanningDeliverer?.dynamicDeliveryPlannings.length ?? 0;
        },
        [tableData, reassignDeliverer, reorderTour],
    );

    // Reassign to `delivererId` and, for a real appro, append the columns to the end of its tour. The referent
    // (null) can scatter them across several appros, so only reassign there. Returns how many rows the server touched.
    const reassignToEnd = useCallback(
        async (planningIds: string[], delivererId: string | null): Promise<number> =>
            delivererId === null
                ? ((await reassignDeliverer({ variables: { planningIds, delivererId } })).data
                      ?.updateDynamicDeliveryPlanningDeliverer?.dynamicDeliveryPlannings.length ?? 0)
                : reassignAndPosition({ planningIds, toApproId: delivererId, insertIndex: Infinity }),
        [reassignDeliverer, reassignAndPosition],
    );

    // "Add" a potential column of an already-visited site to that stop's tour: activate it and assign it to the
    // stop's appro. Same-site grouping then folds it into the right stop automatically.
    const handleAddPotentialColumns = useCallback(
        async (columnPlanningIds: string[], toApproId: string) => {
            if (columnPlanningIds.length === 0) return;
            // An unassigned stop ('none') is delivered by the zone referent → assign to null, not the sentinel.
            const delivererId = toApproId === UNASSIGNED_DROPPABLE_ID ? null : toApproId;
            const approName =
                delivererId == null
                    ? "l'appro référent"
                    : (tableData.find((row) => approIdOf(row) === toApproId)?.assignedDeliverer?.profile.fullname ??
                      delivererNameById.get(toApproId) ??
                      'cet appro');

            const confirmed = await confirmPopup({
                title: 'Ajouter à la livraison',
                message: `Ajouter ${columnPlanningIds.length} colonne(s) potentielle(s) à la tournée de ${approName} ?`,
            });
            if (!confirmed) return;

            try {
                const activations = potentialActivationUpdates(columnPlanningIds, rowsById, dateString);
                if (activations.length > 0) await updatePlanningBatch({ variables: { updates: activations } });
                const affected = await reassignToEnd(columnPlanningIds, delivererId);
                await refetch();
                if (!warnIfPartialMove(columnPlanningIds.length, affected)) {
                    toast.success(`${columnPlanningIds.length} colonne(s) ajoutée(s) à la tournée de ${approName}`);
                }
            } catch (err) {
                toast.error(`Erreur lors de l'ajout : ${err instanceof Error ? err.message : String(err)}`);
            }
        },
        [tableData, delivererNameById, rowsById, dateString, updatePlanningBatch, reassignToEnd, refetch],
    );

    // Edit mode: if the planner removes every chip after arming « Affecter à la suite d'un point », disarm —
    // nothing left to drop. insertIds only re-arms via startAssignAfterPoint, so this can't loop or auto-rearm.
    useEffect(() => {
        if (editMode && insertMode && selectedRegroupementIds.length === 0) setInsertIds([]);
    }, [editMode, insertMode, selectedRegroupementIds]);

    // Appros added by hand as empty drop targets in the tour list, so a regroupement can be dragged onto a
    // deliverer who has no column yet that day. Per-date scratch state: once the drop lands (or the date
    // changes) the appro either appears with real rows or is dropped.
    const [extraApproIds, setExtraApproIds] = useState<string[]>([]);
    useEffect(() => {
        setExtraApproIds([]);
        setEditMode(false);
        setSelectionGroups([]);
        setInsertIds([]);
    }, [dateString]);

    const presentApproIds = useMemo(() => {
        const ids = new Set<string>();
        for (const row of tableData) {
            if (row.isDeliver && row.assignedDeliverer?._id) ids.add(row.assignedDeliverer._id);
        }
        return ids;
    }, [tableData]);

    const extraAppros = useMemo(
        () =>
            extraApproIds
                .filter((id) => !presentApproIds.has(id))
                .map((id) => ({ _id: id, name: delivererNameById.get(id) ?? 'Appro inconnu' })),
        [extraApproIds, presentApproIds, delivererNameById],
    );

    // All deliverers as picker options, sorted once; the "add appro" and "change operator" pickers derive from it.
    const delivererOptions = useMemo(
        () =>
            deliveryPeople
                .map((person) => ({ value: person._id, label: delivererName(person) }))
                .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
        [deliveryPeople],
    );

    const handleAddAppro = useCallback(async () => {
        const options = delivererOptions.filter(
            ({ value }) => !presentApproIds.has(value) && !extraApproIds.includes(value),
        );
        if (options.length === 0) {
            toast.info('Tous les appros sont déjà dans la liste du jour');
            return;
        }
        const choice = await selectOptionPopup({ title: 'Ajouter un appro à la tournée', options });
        if (!choice) return;
        setExtraApproIds((ids) => [...ids, choice.value]);
    }, [delivererOptions, presentApproIds, extraApproIds]);

    // Toolbar « Affecter à un opérateur »: pick an operator (or keep the proposed one), put the selected columns
    // in delivery and reassign in one step — assigning an operator implies delivery, so there is no separate « add
    // to delivery » action. A confirmation recaps the change (incl. how many columns are newly put in delivery).
    const handleAssignOperator = useCallback(async () => {
        if (selectedColumnIds.length === 0) return;
        // Sentinel distinct from null (= zone referent): keep each column's currently-proposed operator and only
        // put it in delivery, without reassigning.
        const KEEP_OPERATOR = '__keep_operator__';
        const choice = await selectOptionPopup<string | null>({
            title: `Affecter ${selectedColumnIds.length} colonne(s) à un opérateur`,
            placeholder: 'Rechercher un appro…',
            options: [
                { value: KEEP_OPERATOR, label: 'Garder l’opérateur proposé', hint: 'met en livraison' },
                { value: null, label: 'Appro référent de la zone' },
                ...delivererOptions,
            ],
        });
        if (!choice) return;

        const activations = potentialActivationUpdates(selectedColumnIds, rowsById, dateString);
        const operatorLabel =
            choice.value === KEEP_OPERATOR
                ? 'leur opérateur proposé'
                : choice.value == null
                  ? 'l’appro référent de la zone'
                  : choice.label;

        const confirmed = await confirmPopup({
            title: 'Affecter à un opérateur',
            message:
                `Affecter ${selectedColumnIds.length} colonne(s) à ${operatorLabel}` +
                (activations.length > 0 ? ` et mettre ${activations.length} colonne(s) en livraison ?` : ' ?'),
        });
        if (!confirmed) return;

        try {
            if (activations.length > 0) await updatePlanningBatch({ variables: { updates: activations } });
            // KEEP keeps each column's operator (no reassign) → nothing to under-report; otherwise check the count.
            const affected =
                choice.value === KEEP_OPERATOR
                    ? selectedColumnIds.length
                    : await reassignToEnd(selectedColumnIds, choice.value);
            await refetch();
            if (!warnIfPartialMove(selectedColumnIds.length, affected)) {
                toast.success(`${selectedColumnIds.length} colonne(s) → ${operatorLabel}`);
            }
            clearSelection();
        } catch (err) {
            toast.error(`Erreur lors de l'affectation : ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [
        selectedColumnIds,
        rowsById,
        delivererOptions,
        dateString,
        updatePlanningBatch,
        reassignToEnd,
        refetch,
        clearSelection,
    ]);

    // Drag a regroupement card: within the same appro → reorder the day's tour (manual override); onto another
    // appro → reassign that regroupement's operator. draggableId = `${sourceApproId}|${rank}`.
    const handleDelivererDragEnd = useCallback(
        async (result: DropResult) => {
            const { source, destination, draggableId } = result;
            if (!destination) return;

            // Same appro → manual per-day tour reorder (overrides the zone default for that date).
            if (destination.droppableId === source.droppableId) {
                if (destination.index === source.index) return;
                const regroupements = orderedRegroupementPlanningIds(tableData, source.droppableId);
                if (source.index >= regroupements.length) return;
                const reordered = [...regroupements];
                const [moved] = reordered.splice(source.index, 1);
                reordered.splice(destination.index, 0, moved);
                const planningIds = reordered.flat();
                try {
                    // Optimistic: write the new tourOrder to the cache synchronously so the dropped card stays
                    // in place instead of snapping back to its old slot while the mutation/refetch round-trips.
                    await reorderTour({
                        variables: { planningIds },
                        optimisticResponse: {
                            reorderDynamicDeliveryPlanningTour: {
                                __typename: 'DynamicDeliveryPlanningUpdateResult',
                                dynamicDeliveryPlannings: planningIds.map((id, index) => ({
                                    __typename: 'DynamicDeliveryPlanning',
                                    _id: id,
                                    tourOrder: index,
                                })),
                            },
                        },
                    });
                    toast.success('Ordre de tournée mis à jour');
                } catch (err) {
                    toast.error(
                        `Erreur lors du réordonnancement : ${err instanceof Error ? err.message : String(err)}`,
                    );
                }
                return;
            }

            const fromAppro = source.droppableId;
            const toAppro = destination.droppableId;
            const { rank } = parseRegroupementKey(draggableId);

            // Same grouping as the tour list (buildGroups) → the reassign targets exactly the dragged card's rows.
            const planningIds = planningIdsOfRegroupement(tableData, fromAppro, rank);
            if (planningIds.length === 0) {
                toast.error('Réaffectation impossible : aucune colonne trouvée pour ce regroupement.');
                return;
            }

            // Target appro name: from a row already on it, or from the deliverers list when the target is an
            // empty appro added by hand. null target → the server resets to the zone referent.
            const targetName =
                toAppro === UNASSIGNED_DROPPABLE_ID
                    ? "l'appro référent"
                    : (tableData.find((row) => row.assignedDeliverer?._id === toAppro)?.assignedDeliverer?.profile
                          .fullname ??
                      delivererNameById.get(toAppro) ??
                      'Appro inconnu');

            const confirmed = await confirmPopup({
                title: "Changer l'appro",
                message: `Réaffecter ${planningIds.length} colonne(s) à ${targetName} ?`,
            });
            if (!confirmed) return;

            try {
                // "Appro référent" (null) can scatter rows across several appros, so we only reassign there;
                // a real target appro also gets the dropped card locked in at its drop position.
                let affected: number;
                if (toAppro === UNASSIGNED_DROPPABLE_ID) {
                    const reassign = await reassignDeliverer({ variables: { planningIds, delivererId: null } });
                    affected =
                        reassign.data?.updateDynamicDeliveryPlanningDeliverer?.dynamicDeliveryPlannings.length ?? 0;
                } else {
                    affected = await reassignAndPosition({
                        planningIds,
                        toApproId: toAppro,
                        insertIndex: destination.index,
                    });
                }
                // Refetch = source of truth: re-reads assignedDeliverer from the DB so the move is confirmed and
                // visible (and reverts cleanly if the server rejected it). No optimistic guesswork.
                await refetch();
                if (!warnIfPartialMove(planningIds.length, affected)) {
                    toast.success(`${planningIds.length} colonne(s) → ${targetName}`);
                }
            } catch (err) {
                toast.error(`Erreur lors de la réaffectation : ${err instanceof Error ? err.message : String(err)}`);
            }
        },
        [tableData, delivererNameById, reassignDeliverer, reassignAndPosition, refetch],
    );

    // A table selection (rows) and a map selection (stops) mean different things, so switching to/from the
    // map clears it to keep each view's selection unambiguous.
    const handleViewChange = useCallback(
        (next: ViewMode) => {
            if (next === view) return;
            if (view === 'map' || next === 'map') {
                clearSelection();
                setFocusedApproIds([]);
                // Edit mode is map-only — leaving (or re-entering) the map drops its regroupement selection.
                setEditMode(false);
                setSelectionGroups([]);
                setInsertIds([]);
            }
            setView(next);
        },
        [view, clearSelection],
    );

    // « Ajouter à une tournée existante » — step 1: snapshot the current selection as the moving payload and
    // switch to the map view (regroupements are clickable there), then wait for the planner to pick a target.
    const startInsertIntoTour = useCallback(() => {
        if (selectedColumnIds.length === 0) return;
        // handleViewChange clears insertIds when entering the map, so switch FIRST then arm the payload — same
        // batch, last write wins; otherwise the payload is wiped before the planner can pick a target.
        if (view !== 'map') handleViewChange('map');
        setInsertIds([...selectedColumnIds]);
    }, [selectedColumnIds, view, handleViewChange]);

    const cancelInsertIntoTour = useCallback(() => setInsertIds([]), []);

    // Step 2: the planner clicked a target regroupement (appro + rank). Insert the moving columns right AFTER
    // it in that appro's tour — activate any still-potential column, reassign all to the appro, reposition.
    const pickInsertTarget = useCallback(
        async (targetApproId: string, targetRank: number) => {
            // In edit mode the payload tracks the live regroupement selection (the recap chips stay removable
            // while picking the target), so read it live here instead of the frozen insertIds snapshot.
            const movingIds = editMode ? selectedRegroupementIds : insertIds;
            if (movingIds.length === 0) return;
            if (targetApproId === UNASSIGNED_DROPPABLE_ID) {
                toast.info("Choisissez la tournée d'un appro assigné.");
                return;
            }

            const targetIds = planningIdsOfRegroupement(tableData, targetApproId, targetRank);
            if (targetIds.length > 0 && targetIds.every((id) => movingIds.includes(id))) {
                toast.info('Choisissez un autre arrêt que ceux déplacés.');
                return;
            }

            // In edit mode the payload is whole regroupements; otherwise it's the column selection.
            const movingCount = editMode ? selectionGroups.length : movingIds.length;
            const movingNoun = editMode ? 'regroupement' : 'colonne';

            const targetName =
                tableData.find((row) => row.assignedDeliverer?._id === targetApproId)?.assignedDeliverer?.profile
                    .fullname ??
                delivererNameById.get(targetApproId) ??
                'cet appro';

            const confirmed = await confirmPopup({
                title: 'Déplacer vers un arrêt',
                message: `Déplacer ${movingCount} ${movingNoun}(s) dans la tournée de ${targetName}, juste après le regroupement ${regroupementRankLabel(targetRank)} ?`,
                body: buildMovePreviewBody(tableData, movingIds, targetApproId),
            });
            if (!confirmed) return;

            setInsertIds([]);
            try {
                // « Déplacer vers un arrêt… » also activates a still-potential column so it really joins the
                // delivery (keyed by columnId+date, the planning _id is stable → reassign/reorder still apply).
                const activations = potentialActivationUpdates(movingIds, rowsById, dateString);
                if (activations.length > 0) await updatePlanningBatch({ variables: { updates: activations } });

                // Insert just after the clicked regroupement in the target appro's ordered tour.
                const groups = orderedRegroupementPlanningIds(tableData, targetApproId).filter(
                    (group) => !group.some((id) => movingIds.includes(id)),
                );
                const targetIdx = groups.findIndex((group) => group.some((id) => targetIds.includes(id)));
                const insertIndex = targetIdx === -1 ? groups.length : targetIdx + 1;

                const affected = await reassignAndPosition({
                    planningIds: movingIds,
                    toApproId: targetApproId,
                    insertIndex,
                });
                await refetch();
                if (!warnIfPartialMove(movingIds.length, affected)) {
                    toast.success(`${movingCount} ${movingNoun}(s) ajouté(s) à la tournée de ${targetName}`);
                }
                clearSelection();
                setSelectionGroups([]);
            } catch (err) {
                toast.error(
                    `Erreur lors de l'ajout à la tournée : ${err instanceof Error ? err.message : String(err)}`,
                );
            }
        },
        [
            insertIds,
            editMode,
            selectionGroups,
            selectedRegroupementIds,
            tableData,
            rowsById,
            dateString,
            delivererNameById,
            updatePlanningBatch,
            reassignAndPosition,
            refetch,
            clearSelection,
        ],
    );

    // Putting columns IN delivery now goes through « Affecter à un opérateur » (assigning implies delivery), so
    // this is the remove-only counterpart: deactivate the still-delivered columns in the selection.
    const removeFromDelivery = useCallback(async () => {
        if (selectedColumnIds.length === 0) return;

        const confirmed = await confirmPopup({
            title: 'Retirer de la livraison',
            message: `Retirer ${selectedColumnIds.length} colonne(s) de la livraison ?`,
        });
        if (!confirmed) return;

        const updates = selectedColumnIds
            .map((id) => rowsById.get(id))
            .filter((row): row is DynamicDeliveryRow => !!row && row.isActive)
            .map((row) => ({ columnId: row.columnId, date: dateString, isActive: false }));

        if (updates.length === 0) {
            toast.info('Aucune modification nécessaire (colonnes déjà hors livraison)');
            clearSelection();
            return;
        }

        try {
            await updatePlanningBatch({ variables: { updates } });
            toast.success(`${updates.length} colonne(s) retirée(s) de la livraison`);
            clearSelection();
        } catch (err) {
            toast.error(`Erreur lors de la sauvegarde: ${err instanceof Error ? err.message : String(err)}`);
        }
    }, [selectedColumnIds, rowsById, dateString, updatePlanningBatch, clearSelection]);

    const handleRegeneratePlanning = useCallback(
        async (overrideManualChanges: boolean) => {
            try {
                const result = await regeneratePlannings({ variables: { date: dateString, overrideManualChanges } });
                const payload = result.data?.regenerateDynamicDeliveryPlannings;

                if (!payload?.success) {
                    toast.error(`Échec de la régénération${payload?.error ? ` : ${payload.error}` : ''}`);
                    return;
                }

                toast.success('Planning régénéré');
                await refetch();
            } catch (err) {
                toast.error(`Erreur lors de la régénération : ${err instanceof Error ? err.message : String(err)}`);
            }
        },
        [regeneratePlannings, refetch, dateString],
    );

    const hasSelection = selectedColumnIds.length > 0;
    // Flat list of column-selection actions, surfaced as buttons by PlanningActionsToolbar on every view.
    const columnActions = useMemo(
        () => [
            {
                icon: <Users />,
                label: `Affecter à un opérateur${hasSelection ? ` (${selectedColumnIds.length})` : ''}`,
                onClick: handleAssignOperator,
                hidden: !hasSelection,
                dataTest: 'planning-action-assign-operator',
            },
            {
                icon: <ArrowDownToLine />,
                label: `Retirer de la livraison${hasSelection ? ` (${selectedColumnIds.length})` : ''}`,
                onClick: removeFromDelivery,
                hidden: !hasSelection,
                dataTest: 'planning-action-remove-from-delivery',
            },
            {
                icon: <ListPlus />,
                label: `Déplacer vers un arrêt…${hasSelection ? ` (${selectedColumnIds.length})` : ''}`,
                onClick: startInsertIntoTour,
                hidden: !hasSelection || insertMode,
                dataTest: 'planning-action-move-to-stop',
            },
            {
                icon: <X />,
                label: 'Tout désélectionner',
                onClick: clearSelection,
                hidden: !hasSelection,
                dataTest: 'planning-action-clear-selection',
            },
        ],
        [
            selectedColumnIds.length,
            hasSelection,
            insertMode,
            removeFromDelivery,
            handleAssignOperator,
            startInsertIntoTour,
            clearSelection,
        ],
    );

    const isLoading = loading || totemClosedDaysLoading;

    if (isLoading) {
        return (
            <MainContainer>
                <Loader />
            </MainContainer>
        );
    }

    if (error || totemClosedDaysError) {
        throw new Error('Une erreur est survenue lors de la récupération des données');
    }

    // Built once, placed differently per view: in the table view it rides inside the table's scroll viewport
    // (so it scrolls away with the rows and frees screen space); in the map view it stays above the fixed split.
    const summaryCards = (
        <SummaryCards
            aggregate={dayAggregate}
            tours={tourSummary}
            urgentFilterActive={urgentFilterActive}
            onToggleUrgentFilter={toggleUrgentFilter}
            onShowMap={() => handleViewChange('map')}
        />
    );

    // View switcher (Par appro / Par site / Carte) + collapse-all. Built once, then pinned below the cards in
    // each view (sticky inside the table's scroll viewport, or sticky in the map view's scroll container).
    const viewToolbar = (
        <ViewToolbar>
            <ViewSwitcher role="tablist" aria-label="Vue du planning">
                {VIEW_OPTIONS.map((option) => (
                    <Button
                        key={option.id}
                        type="button"
                        size="sm"
                        variant={view === option.id ? 'default' : 'outline'}
                        onClick={() => handleViewChange(option.id)}
                        data-test={`dynamic-delivery-view-${option.id}`}
                    >
                        {option.label}
                    </Button>
                ))}
            </ViewSwitcher>
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={toggleCollapseAll}
                data-test="dynamic-delivery-toggle-all"
            >
                <ChevronDown
                    size={14}
                    style={{ transform: allCollapsed ? 'rotate(-90deg)' : 'none', marginRight: 4 }}
                />
                {allCollapsed ? 'Tout déplier' : 'Tout replier'}
            </Button>
        </ViewToolbar>
    );

    const insertBanner = (
        <InsertBanner data-test="planning-insert-banner">
            <InsertBannerText>
                <ListPlus size={16} />
                <span>
                    Cliquez sur l’arrêt cible (carte ou liste) pour y insérer{' '}
                    <b>{editMode ? `${selectionGroups.length} regroupement(s)` : `${insertIds.length} colonne(s)`}</b>,
                    juste après cet arrêt.
                </span>
            </InsertBannerText>
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={cancelInsertIntoTour}
                data-test="planning-insert-cancel"
            >
                Annuler
            </Button>
        </InsertBanner>
    );

    // Shared horizontal action menu (every view). Column actions always; the « mode édition » controls only on the
    // Carte view, where stops are clickable. Renders nothing off-map when there is no selection.
    const renderActionsToolbar = (showEditMode: boolean) => (
        <PlanningActionsToolbar
            showEditMode={showEditMode}
            editMode={editMode}
            onToggleEditMode={toggleEditMode}
            regroupementCount={selectionGroups.length}
            onAssignAfterPoint={startAssignAfterPoint}
            onReorderSelected={handleReorderSelected}
            canReorder={canReorder}
            assignTargetMode={insertMode}
            columnActions={columnActions}
        />
    );

    return (
        <MainContainer>
            <PlanningHeader
                selectedDate={selectedDate}
                onChangeDate={setSelectedDate}
                regenerating={regenerating}
                onRegenerate={handleRegeneratePlanning}
                showRouteMode={view === 'map'}
                routeMode={mapRouteMode}
                onChangeRouteMode={setMapRouteMode}
            />
            {view === 'map' ? (
                // Map view: the switcher/toolbar sits at the top, the list+map split fills the viewport. No summary
                // cards here (they stay on the table views) — keeps the map as large as possible.
                <MapContentContainer>
                    <MapStickyToolbar>
                        <MapToolbarRow>
                            {viewToolbar}
                            {renderActionsToolbar(true)}
                        </MapToolbarRow>
                        <PlanningMoveRecap chips={selectedRegroupementChips} onRemove={toggleRegroupement} />
                        {insertMode && insertBanner}
                    </MapStickyToolbar>
                    <MapBody>
                        <PlanningMapView
                            rows={tableData}
                            selectedKeys={mapSelectedKeys}
                            onSelect={selectColumns}
                            focusedApproIds={focusedApproIds}
                            onFocusAppro={toggleApproFocus}
                            extraAppros={extraAppros}
                            onAddAppro={handleAddAppro}
                            collapseAll={allCollapsed}
                            collapseToken={collapseToken}
                            insertMode={insertMode}
                            onPickRegroupement={pickInsertTarget}
                            onDragEnd={handleDelivererDragEnd}
                            editMode={editMode}
                            selectionNumberByKey={selectionNumberByKey}
                            onToggleRegroupement={toggleRegroupement}
                            onToggleAllFocus={toggleAllApproFocus}
                            onAddPotential={handleAddPotentialColumns}
                            roadRouting={mapRouteMode === 'road'}
                        />
                    </MapBody>
                </MapContentContainer>
            ) : (
                <ContentContainer>
                    <Body>
                        <PlanningTableView
                            topBanner={summaryCards}
                            stickyToolbar={
                                <>
                                    {viewToolbar}
                                    {renderActionsToolbar(false)}
                                </>
                            }
                            view={view}
                            columns={columns}
                            data={displayData}
                            hasActiveFilters={hasActiveFilters}
                            expanded={expanded}
                            onExpandedChange={setExpanded}
                            onFiltersChange={handleFiltersChange}
                            filtersApiRef={filtersApiRef}
                            approColors={approColors}
                            selectedKeys={selectedKeys}
                            onSelectionChange={setSelectedKeys}
                            isLoading={saveLoading}
                            anomalies={anomalies}
                            anomalyColumnCount={anomalyColumnCount}
                            hiddenPotentialCount={hiddenPotentialCount}
                            potentialCount={dayAggregate.potentialCount}
                        />
                    </Body>
                </ContentContainer>
            )}
        </MainContainer>
    );
};

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
    overflow: hidden;
`;

const ContentContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 16px;
    gap: 16px;
`;

const Body = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    min-height: 0;
    overflow: hidden;
`;

// Map view: a single vertical scroll so the cards scroll away (like the table view), instead of the fixed split
// that pinned them. No top padding so the sticky toolbar pins flush to the top with nothing showing above it.
const MapContentContainer = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 16px 16px;
`;

// View switcher / actions toolbar pinned at the top of the map view; the list+map split fills the rest.
const MapStickyToolbar = styled.div`
    position: sticky;
    top: 0;
    z-index: 30;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 0;
    background: ${({ theme }) => theme.backgroundColor};
`;

// Sélecteur de vue (gauche) + actions « mode édition »/colonnes (droite) sur une seule rangée.
const MapToolbarRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

// list + map split: fills the remaining height after the cards/toolbar and clips its content (overflow hidden), so
// the map stays full-height and visible while the tour list scrolls INTERNALLY (its own overflow-y). No page scroll.
const MapBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    gap: 12px;
`;

const ViewToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ViewSwitcher = styled.div`
    display: flex;
    gap: 8px;
`;

// Insertion-mode instruction bar (« Ajouter à une tournée existante »): visible while the planner picks the
// target regroupement, with a clear way out.
const InsertBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border: 1px solid ${({ theme }) => theme.ctaPrimaryColor};
    border-radius: 10px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
`;

const InsertBannerText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;

    svg {
        color: ${({ theme }) => theme.ctaPrimaryColor};
        flex-shrink: 0;
    }

    b {
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

// Before/after summary inside the move confirmation dialog.
const PreviewBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 4px;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const PreviewBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const PreviewTitle = styled.div`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const PreviewLine = styled.div`
    color: ${({ theme }) => theme.textColor};
`;

const PreviewFrom = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;

const PreviewRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

const PreviewImpact = styled.span<{ $over: boolean }>`
    white-space: nowrap;
    font-weight: ${({ $over }) => ($over ? 700 : 500)};
    color: ${({ theme, $over }) => ($over ? theme.errorColor : theme.textColor)};
`;
